<template>
  <div id="HomePage">
    <section class="section">
      <div class="container">
        <div v-if="isDashboard && dashboard" style="padding-bottom: 3rem;">
          <h1
            class="title is-primary"
            style="color: #00AEEF; font-weight: 100;"
          >
            {{ $t("home.dashboard.title") }}
          </h1>
          <h1 class="subtitle is-6">
            {{ $t("home.dashboard.updated") }}: {{ selectedDate }}
          </h1>

          <div class="columns">
            <div class="column is-4">
              <b-message
                type="is-primary"
                :title="$t('home.dashboard.prospect_title')"
                :closable="false"
              >
                <p class="title is-2 center-content">
                  {{ dashboard.prospect.in_area + dashboard.prospect.out_area }}
                </p>
                <p class="subtitle is-6 center-content">
                  <span>{{ $t("home.dashboard.total_prospect") }}</span>
                </p>
                <hr />
                <div class="columns is-mobile">
                  <div class="column">
                    <p
                      class="title is-2 center-content"
                      :style="
                        dashboard.prospect.in_area > 0
                          ? 'color:#23d160'
                          : 'black'
                      "
                    >
                      {{ dashboard.prospect.in_area }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.in_area") }}</span>
                    </p>
                  </div>
                  <div class="column">
                    <p
                      class="title is-2 center-content"
                      :style="
                        dashboard.prospect.out_area > 0
                          ? 'color:#ff0040'
                          : 'black'
                      "
                    >
                      {{ dashboard.prospect.out_area }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.out_area") }}</span>
                    </p>
                  </div>
                </div>
              </b-message>
            </div>

            <div class="column is-4">
              <b-message
                type="is-primary"
                :title="$t('home.dashboard.call_title')"
                :closable="false"
              >
                <p class="title is-2 center-content">
                  {{ dashboard.called.new + dashboard.called.old }}
                </p>
                <p class="subtitle is-6 center-content">
                  <span>{{ $t("home.dashboard.total_call") }}</span>
                </p>
                <hr />
                <div class="columns is-mobile">
                  <div class="column">
                    <p
                      class="title is-2 center-content"
                      :style="
                        dashboard.accepted.new + dashboard.accepted.old > 0
                          ? 'color:#23d160'
                          : 'black'
                      "
                    >
                      {{ dashboard.accepted.new + dashboard.accepted.old }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.call_accept") }}</span>
                    </p>
                  </div>
                  <div class="column">
                    <p
                      class="title is-2 center-content"
                      :style="
                        dashboard.rejected.new + dashboard.rejected.old > 0
                          ? 'color:#ff0040'
                          : 'black'
                      "
                    >
                      {{ dashboard.rejected.new + dashboard.rejected.old }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.call_reject") }}</span>
                    </p>
                  </div>
                </div>
              </b-message>
            </div>

            <div class="column is-4">
              <b-message
                type="is-primary"
                :title="$t('home.dashboard.new_customer_title')"
                :closable="false"
              >
                <p class="title is-2 center-content">
                  {{ dashboard.customer.prospect + dashboard.customer.sale }}
                </p>
                <p class="subtitle is-6 center-content">
                  <span>{{ $t("home.dashboard.new_customer_title") }}</span>
                </p>
                <hr />
                <div class="columns is-mobile">
                  <div class="column">
                    <p class="title is-2 center-content">
                      {{ dashboard.customer.prospect }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.from_prospect") }}</span>
                    </p>
                  </div>
                  <div class="column">
                    <p class="title is-2 center-content">
                      {{ dashboard.customer.sale }}
                    </p>
                    <p class="subtitle is-6 center-content">
                      <span>{{ $t("home.dashboard.from_sale") }}</span>
                    </p>
                  </div>
                </div>
              </b-message>
            </div>
          </div>

          <!-- Vue Slider -->
          <vue-slider
            v-model="selectedDate"
            :data="dateRangeData"
            :lazy="true"
            tooltip="always"
            tooltipPlacement="bottom"
            @change="updateFunnelInfo"
          />
        </div>

        <!-- HOME PAGE MENU -->
        <h1
          class="title is-primary"
          style="color: #00AEEF; font-weight: 100; margin-bottom: 8px"
        >
          {{ $t("home.title") }}
        </h1>
        <div class="columns is-gapless is-multiline is-tablet is-mobile">
          <!-- Customer Registration -->
          <div
            v-if="isRegister"
            class="column is-3-desktop is-4-tablet is-6-mobile"
          >
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('register')"
              >
                <b-icon
                  icon="home-map-marker"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />
                <p class="subtitle">{{ $t("home.register") }}</p>
              </article>
            </div>
          </div>

          <!-- Admin Users Management -->
          <div
            v-if="isAdminUserManage"
            class="column is-3-desktop is-4-tablet is-6-mobile"
          >
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('users')"
              >
                <b-icon
                  icon="account-group"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />

                <p class="subtitle">{{ $t("home.admins") }}</p>
              </article>
            </div>
          </div>
          <!-- Customers Management -->
          <div
            v-if="isCustomerManage"
            class="column is-3-desktop is-4-tablet is-6-mobile"
          >
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('customers')"
              >
                <b-icon
                  icon="account-search"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />
                <p class="subtitle">{{ $t("home.customers") }}</p>
              </article>
            </div>
          </div>
          <!-- Prospect Customers -->
          <div
            v-if="isProspectCustomer"
            class="column is-3-desktop is-4-tablet is-6-mobile"
          >
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('prospects')"
              >
                <b-icon
                  icon="account-heart"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />
                <p class="subtitle">{{ $t("home.prospect") }}</p>
              </article>
            </div>
          </div>
          <!-- Devices Management -->
          <div
            v-if="isAdminUserManage"
            class="column is-3-desktop is-4-tablet is-6-mobile"
          >
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('devices')"
              >
                <b-icon
                  icon="robot"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />
                <p class="subtitle">{{ $t("home.devices") }}</p>
              </article>
            </div>
          </div>
          <!-- My Profile -->
          <div class="column is-3-desktop is-4-tablet is-6-mobile">
            <div class="tile is-parent is-hoverable">
              <article
                class="tile is-child box"
                @click="navigateToPageName('myprofile')"
              >
                <b-icon
                  icon="settings"
                  size="is-large"
                  type="is-primary"
                  style="margin: 0.5rem 0;"
                />
                <p class="subtitle">{{ $t("home.profile") }}</p>
              </article>
            </div>
          </div>
        </div>
        <!-- END: HOME PAGE MENU -->
      </div>
    </section>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { mapState, mapMutations } from "vuex";
import AlertManager from "@/managers/AlertManager";
import UserApiManager from "@/apis/UserApiManager";

export default {
  name: "HomePage",
  data() {
    return {
      updatedDate: null,
      selectedDate: dayjs(new Date()).format("DD/MM/YYYY"),
      dateRangeData: [],
      dashboard: null,
      isCurrentDate: false,
      isDashboard: false,
      isRegister: false,
      isPlanning: false,
      isCustomerManage: false,
      isAdminUserManage: false,
      isProspectCustomer: false
    };
  },
  computed: mapState(["userProfile", "wsMessage"]),
  watch: {
    userProfile: {
      handler: function(newProfile) {
        this.setUpHomeCardMenuVisibility(newProfile.roles);
      },
      deep: true
    },
    wsMessage: {
      handler: function(newSocketMessage) {
        if (newSocketMessage.event) {
          switch (newSocketMessage.event) {
            case "update":
              this.dashboard = newSocketMessage.payload;
              this.updatedDate = dayjs(newSocketMessage.time).format(
                "DD/MM/YYYY @ HH:mm"
              );
              break;
            default:
              break;
          }
        }
      },
      deep: true
    }
  },
  filters: {
    dateformatter: function(value) {
      if (!value) return "";
      return dayjs(value).format("DD/MM/YYYY @ HH:mm");
    }
  },
  methods: {
    ...mapMutations({
      setUserProfile: "SET_USER_PROFILE"
    }),
    navigateToPageName: function(name) {
      switch (name) {
        case "users":
        case "register":
        case "customers":
        case "prospects":
        case "myprofile":
        case "devices":
          this.$router.push({ name });
          break;

        default:
          AlertManager.showErrorSnackbar("Wrong Navigation Menu");
          break;
      }
    },
    generateBackwardDate: function(backwardDays) {
      this.dateRangeData = [];
      for (let i = backwardDays; i > 0; i--) {
        let date = new Date();
        date.setDate(date.getDate() - (i - 1));
        this.dateRangeData.push(dayjs(date).format("DD/MM/YYYY"));
      }
    },
    getFunnelInfo: async function(dateString) {
      const funnelResponse = await UserApiManager.requestInitialFunnel(
        dateString
      );
      switch (funnelResponse.code) {
        case "0":
          if (funnelResponse.data.funnel) {
            this.dashboard = funnelResponse.data.funnel;
          }
          break;

        default:
          AlertManager.showErrorSnackbar(funnelResponse.message);
          break;
      }
    },
    updateFunnelInfo: function(value) {
      const currentDate = dayjs(Date()).format("DD-MM-YYYY");
      const selectedDate = value.split("/").join("-");
      if (currentDate === selectedDate) {
        this.isCurrentDate = true;
        const wsParam = { topic: "funnel", action: "add" };
        this.$emit("ws_update", JSON.stringify(wsParam));
      } else {
        this.isCurrentDate = false;
        const wsParam = { topic: "funnel", action: "remove" };
        this.$emit("ws_update", JSON.stringify(wsParam));
      }
      this.getFunnelInfo(selectedDate);
    },
    getCurrentUserProfile: async function() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const loggedInUser = await UserApiManager.requestGetCurrentUser();
      loadingComponent.close();
      this.setUserProfile(loggedInUser);
      this.setUpHomeCardMenuVisibility(loggedInUser.roles);
    },
    setUpHomeCardMenuVisibility: function(userRoles) {
      this.isDashboard = userRoles.report;
      this.isRegister = userRoles.customer_register;
      this.isPlanning = userRoles.map_area;
      this.isCustomerManage = userRoles.customer_list;
      this.isAdminUserManage = userRoles.user_manage;
      this.isProspectCustomer = userRoles.customer_propect;
      if (this.isDashboard) {
        const currentDate = dayjs(Date()).format("DD-MM-YYYY");
        this.getFunnelInfo(currentDate);
        this.generateBackwardDate(30);
        // Requset Web Socket
        const wsParam = { topic: "funnel", action: "add" };
        this.$emit("ws_update", JSON.stringify(wsParam));
      }
    }
  },
  mounted() {
    if (this.userProfile.name) {
      this.setUpHomeCardMenuVisibility(this.userProfile.roles);
    } else {
      this.getCurrentUserProfile();
    }
  },
  beforeRouteLeave(to, from, next) {
    const wsParam = { topic: "funnel", action: "remove" };
    this.$emit("ws_update", JSON.stringify(wsParam));
    next();
  }
};
</script>

<style scoped>
.center-content {
  text-align: center;
}
.bounce-enter-active {
  animation: bounce-in 0.5s;
}
.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
